/* eslint-disable max-lines */
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  FormController,
  FormLabel,
  Input,
  SkeletonBlock,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Checkmark } from '@one-thd/sui-icons';
import { BrowserMultiFormatReader } from '@zxing/library';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, {
  Dispatch,
  JSX,
  SetStateAction,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AddInstoreReceipt } from '../../../../api/oms/order-history';
import {
  inStoreErrors,
  scanReceiptErrors,
} from '../../../../constants/receipt-error';
import { useCommonContext } from '../../../../hooks/useContexts';
import { IAddStoreReceiptRequest } from '../../../../models/purchase-history';
import {
  IFormData,
  IReceiptForm,
} from '../../../../models/purchase-history/add-receipt';
import { pushEventInfo } from '../../../../utils/analytics';
import {
  addHyphens,
  addSlash,
  makeInStoreRequestBody,
} from '../../../../utils/order-history';
import { handleDisableBtn } from '../../../../utils/order-history/receipt-utils';

export const ReceiptScanner = ({
  closeDrawer,
  setAlertState,
  setDrawerStepper,
  svocId,
}: IReceiptForm) => {
  const { userID } = useThdCustomer();
  const [videoStream, setVideoStream] = useState(false);
  const [inputError, setInputError] = useState({
    receiptNumber: '',
    salesDate: '',
    preTaxAmount: '',
  });
  const [scanSettings, setScanSettings] = useState({
    error: false,
    tryAgain: false,
    successful: false,
  });

  const { isMobile } = useCommonContext();

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      preTaxAmount: '',
      receiptNumber: '',
      salesDate: '',
      tryAgain404: false,
    },
  });
  const watchReceiptNumber = watch('receiptNumber');
  const watchSalesDate = watch('salesDate');
  const watchPreTaxAmount = watch('preTaxAmount');

  useEffect(() => {
    if (watchReceiptNumber.length === 16) {
      setInputError((prevState) => ({
        ...prevState,
        receiptNumber: 'success',
      }));
      clearErrors('receiptNumber');
    }
    if (watchReceiptNumber.length !== 16) {
      setInputError((prevState) => ({
        ...prevState,
        receiptNumber: 'error',
      }));
      setError('receiptNumber', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchReceiptNumber]);

  useEffect(() => {
    if (watchSalesDate.length === 8) {
      const tempYear = watchSalesDate.split('/');
      const tempDate = new Date(
        `${tempYear[2] < '20' ? '19' : '20'}${tempYear[2]}/${tempYear[0]}/${
          tempYear[1]
        }`,
      );
      if (Number.isNaN(tempDate?.getTime())) {
        setInputError((prevState) => ({
          ...prevState,
          salesDate: 'error',
        }));
        setError('salesDate', { type: 'invalid', message: 'invalid entry' });
        return;
      }
      setInputError((prevState) => ({
        ...prevState,
        salesDate: 'success',
      }));
      clearErrors('salesDate');
      return;
    }
    if (watchSalesDate.length !== 8) {
      setInputError((prevState) => ({
        ...prevState,
        salesDate: 'error',
      }));
      setError('salesDate', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchSalesDate]);

  useEffect(() => {
    if (watchPreTaxAmount.length > 3 && watchPreTaxAmount?.at(-3) === '.') {
      setInputError((prevState) => ({
        ...prevState,
        preTaxAmount: 'success',
      }));
      clearErrors('preTaxAmount');
      return;
    }
    if (
      inputError.preTaxAmount === 'success' &&
      (watchPreTaxAmount.length < 3 || watchPreTaxAmount?.at(-3) !== '.')
    ) {
      setInputError((prevState) => ({
        ...prevState,
        preTaxAmount: 'error',
      }));
      setError('preTaxAmount', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchPreTaxAmount]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setScanSettings((prevState) => ({ ...prevState, error: true }));
      setVideoStream(false);
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [scanSettings.tryAgain]);

  const onFormSubmit: SubmitHandler<IFormData> = async ({
    preTaxAmount,
    receiptNumber,
    salesDate,
  }) => {
    clearErrors();
    try {
      let request: IAddStoreReceiptRequest | null = null;
      if (preTaxAmount && receiptNumber && salesDate && svocId) {
        request = makeInStoreRequestBody(
          svocId,
          receiptNumber,
          salesDate,
          preTaxAmount,
        );

        const response = await AddInstoreReceipt(request, userID);
        const status = response ? response.status : null;
        if (status === null) {
          setError('tryAgain404', {
            type: 'failed',
            message: 'failed request',
          });
          return;
        }
        pushEventInfo({
          category: { primaryCategory: 'button submission' },
          eventInfo: { eventName: 'my account settings' },
          myAccount: {
            settings: 'add receipt: scan receipt: receipt add successful',
          },
        });
        setAlertState({
          alertMessage: `You've successfully added an in-store receipt to your account. View Receipt ${receiptNumber}`,
          status: 'success',
        });
        closeDrawer();
        reset();
        return;
      }
      setError('tryAgain404', { type: 'failed', message: 'failed request' });
    } catch (error) {
      setError('tryAgain404', {
        type: 'failed',
        message: 'failed request',
      });
    }
  };

  interface IBarcodeScanner {
    setData?: Dispatch<SetStateAction<unknown>>;
    setError?: boolean;
    onResult?: (result: string, error: string) => void;
    setScannerText?: (text: string) => void;
    stopStream?: boolean;
  }

  const BarcodeScanner = ({ setScannerText }: IBarcodeScanner): JSX.Element => {
    const [barcodeText, setBarcodeText] = useState<string>('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_barcodeError, setBarcodeError] = useState<string | null>(null);
    const videoId = 'video';

    useEffect(() => {
      setVideoStream(true);
      const codeReader = new BrowserMultiFormatReader();

      if (navigator.mediaDevices) {
        codeReader.listVideoInputDevices().then(() => {
          codeReader
            .decodeOnceFromVideoDevice(undefined, videoId)
            .then((result) => {
              if (result) {
                setBarcodeText(result.getText() as string);
              }
            })
            .catch((err) => {
              if (err) {
                setBarcodeError(err);
              }
            });
        });
      } else {
        setBarcodeError('Camera permissions denied');
      }

      return () => {
        setVideoStream(false);
        codeReader?.reset();
      };
    }, []);

    useEffect(() => {
      if (barcodeText !== '') {
        if (setScannerText) setScannerText(barcodeText);
      }
    }, [barcodeText]);

    return (
      <div
        className="sui-w-full sui-overflow-hidden sui-relative"
        style={{ paddingTop: '100%' }}
        data-testid="receipt-barcode-scanner"
      >
        <video
          id={videoId}
          className="sui-top-0 sui-left-0 sui-w-full sui-h-full sui-block sui-overflow-hidden sui-absolute"
          muted
        />
      </div>
    );
  };

  const renderScanner = () => {
    if (typeof window === 'undefined') return null;
    return (
      <Suspense
        fallback={
          <div
            className="sui-relative sui-w-full sui-overflow-hidden sui-rounded"
            style={{ paddingTop: '100%' }}
            aria-busy="true"
          >
            <div className="sui-absolute sui-inset-0">
              <SkeletonBlock aspect="square" />
            </div>
          </div>
        }
      >
        <BarcodeScanner
          onResult={(_err: string, result: string) => {
            if (result) {
              pushEventInfo({
                category: { primaryCategory: 'button submission' },
                eventInfo: { eventName: 'my account settings' },
                myAccount: {
                  settings:
                    'add receipt: scan receipt: barcode scan successful',
                },
              });
              handleScannerData(result);
            }
          }}
          setError={scanSettings.error}
          setScannerText={handleScannerData}
          stopStream={scanSettings.error || videoStream === false}
        />
      </Suspense>
    );
  };

  const calculateCheckDigit = (
    registerNumber: string,
    transactionId: string,
  ) => {
    let value = `0${registerNumber}${transactionId}0`;
    // accept only digits, dashes or spaces
    if (/[^0-9-\s]+/.test(value)) return '';

    // The Luhn Algorithm to calculate the check digit
    let checkDigit = 0;
    let digit = 0;
    let isEven = false;
    value = value.replace(/\D/g, '');

    for (let n = value.length - 1; n >= 0; n -= 1) {
      digit = parseInt(value.charAt(n), 10); // Ensure digit is a number

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      checkDigit += digit;
      isEven = !isEven;
    }
    checkDigit %= 10;
    if (checkDigit !== 0) checkDigit = 10 - checkDigit;
    return checkDigit.toString();
  };

  const handleScannerData = (textResult: string) => {
    const scannedText = textResult;
    setScanSettings((prevState) => ({ ...prevState, successful: true }));

    const salesDate = scannedText.slice(0, 4) + 2 + scannedText.charAt(4);

    /**
     * Constants for the receipt number
     */
    const storeNumber = scannedText.slice(11, 15);
    const registerNumber = scannedText.slice(5, 7).padStart(5, '0');
    const transactionId = scannedText.slice(7, 11);
    const checkDigit = calculateCheckDigit(registerNumber, transactionId);

    const receiptNumber =
      storeNumber + registerNumber + transactionId + checkDigit;

    setValue('receiptNumber', addHyphens(receiptNumber.toUpperCase()));
    setValue('salesDate', addSlash(salesDate));
  };

  return (
    <>
      <DrawerHeader onClose={closeDrawer}>
        <Typography variant="h4">Scan Receipt</Typography>
      </DrawerHeader>
      {scanSettings.successful ? (
        <>
          <DrawerBody
            style={{
              height: isMobile ? '65vh' : '',
              overflowY: isMobile ? 'auto' : 'visible',
              paddingBottom: isMobile ? '200px' : '',
            }}
          >
            <div className="sui-flex sui-gap-3 sui-items-center">
              <Typography variant="body-base">
                Barcode Scan Successful
              </Typography>
              <Checkmark size="small" color="success" />
            </div>
            <FormController
              className="sui-mt-6"
              fullWidth
              status={
                inputError.receiptNumber || (errors?.receiptNumber && 'error')
              }
            >
              <FormLabel htmlFor="receiptNumber">
                Receipt Number<sup>*</sup>
              </FormLabel>
              <Input
                aria-describedby="receiptNumber-input"
                fullWidth
                id="receiptNumber"
                inputProps={{
                  maxLength: 16,
                }}
                {...register('receiptNumber', {
                  required: true,
                  minLength: 15,
                  onChange: (event: { target: { value: string } }) =>
                    setValue(
                      'receiptNumber',
                      addHyphens(event.target.value.toUpperCase()),
                    ),
                  onBlur: (event: { target: { value: string } }) =>
                    setValue(
                      'receiptNumber',
                      addHyphens(event.target.value.toUpperCase()),
                    ),
                })}
                placeholder="XXXX-XXXXX-XXXXX"
              />
              {(inputError.receiptNumber === 'error' ||
                errors.receiptNumber) && (
                <Typography variant="body-xs" color="danger">
                  {errors?.receiptNumber?.type === 'tryAgain' &&
                    inStoreErrors.receiptNumberNotFound}
                  {(['required', 'minLength', 'invalid', 'pattern'].includes(
                    errors?.receiptNumber?.type ?? 'invalid',
                  ) ||
                    inputError.receiptNumber === 'error') &&
                    inStoreErrors.receiptNumberInvalidFormat}
                </Typography>
              )}
            </FormController>
            <FormController
              className="sui-mt-6"
              fullWidth
              status={inputError.salesDate || (errors?.salesDate && 'error')}
            >
              <FormLabel htmlFor="salesDate">
                Purchase Date<sup>*</sup>
              </FormLabel>
              <Input
                aria-describedby="salesDate-input"
                fullWidth
                id="salesDate"
                inputProps={{
                  maxLength: 8,
                }}
                {...register('salesDate', {
                  required: true,
                  minLength: 7,
                  pattern: /^(\d{2})\/(\d{2})\/(\d{2})$/,
                  onChange: (event: { target: { value: string } }) =>
                    setValue('salesDate', addSlash(event.target.value)),
                  onBlur: (event: { target: { value: string } }) =>
                    setValue('salesDate', addSlash(event.target.value)),
                })}
                placeholder="MM/DD/YY"
              />
              {(inputError.salesDate === 'error' || errors.salesDate) && (
                <Typography variant="body-xs" color="danger">
                  {errors?.salesDate?.type === 'tryAgain' &&
                    inStoreErrors.salesDateNotFound}
                  {(['required', 'minLength', 'invalid', 'pattern'].includes(
                    errors?.salesDate?.type ?? 'invalid',
                  ) ||
                    inputError.salesDate === 'error') &&
                    inStoreErrors.salesDateInvalidFormat}
                </Typography>
              )}
            </FormController>
            <FormController
              className="sui-my-4"
              fullWidth
              status={
                inputError.preTaxAmount || (errors?.preTaxAmount && 'error')
              }
            >
              <FormLabel htmlFor="preTaxAmount">
                Pre-Tax Amount<sup>*</sup>
              </FormLabel>
              <Input
                aria-describedby="preTaxAmount-input"
                fullWidth
                id="preTaxAmount"
                {...register('preTaxAmount', {
                  required: true,
                  minLength: 3,
                  pattern: /(\d+\.\d{1,2})/,
                  onChange: (event: { target: { value: string } }) =>
                    setValue('preTaxAmount', event.target.value),
                  onBlur: (event: { target: { value: string } }) =>
                    setValue('preTaxAmount', event.target.value),
                })}
                placeholder="$"
              />
              {(inputError.preTaxAmount === 'error' || errors.preTaxAmount) && (
                <Typography variant="body-xs" color="danger">
                  {scanReceiptErrors.preTaxAmountInvalidFormat}
                </Typography>
              )}
              {errors.tryAgain404 && (
                <div className="sui-mt-6">
                  <Typography color="danger">
                    {inStoreErrors.api404Error}
                  </Typography>
                </div>
              )}
            </FormController>
          </DrawerBody>
        </>
      ) : (
        <>
          <DrawerBody style={{ height: isMobile ? '65vh' : '' }}>
            <div className="sui-pb-4">
              <Typography variant="body-base">
                Please position your camera to ensure that the barcode fills the
                entire screen and is centered.
              </Typography>
            </div>
            {scanSettings.error ? (
              <div
                style={{
                  width: '100%',
                  height: '221px',
                  backgroundColor: '#000',
                }}
              />
            ) : (
              <div>{renderScanner()}</div>
            )}
            {scanSettings.error && (
              <div className=" sui-pt-4">
                <Typography variant="body-base" weight="bold">
                  We couldn&apos;t quite read that
                </Typography>
                <Typography variant="body-base">
                  Hold the camera steady and try scanning again, or you can
                  manually type in your receipt details.
                </Typography>
              </div>
            )}
          </DrawerBody>
        </>
      )}
      <DrawerFooter position="end">
        <div className="card-fix sui-container sui-flex sui-flex-col sui-gap-3">
          <Button
            onClick={
              scanSettings.successful
                ? handleSubmit(onFormSubmit)
                : () => {
                    if (setDrawerStepper) setDrawerStepper(1);
                  }
            }
            disabled={
              scanSettings.successful
                ? handleDisableBtn(errors, inputError)
                : !scanSettings.error
            }
            variant="primary"
          >
            {scanSettings.successful ? 'OK' : 'Manually Add Receipt'}
          </Button>
          <Button
            onClick={
              scanSettings.successful
                ? closeDrawer
                : () => {
                    setScanSettings((prevState) => ({
                      ...prevState,
                      error: false,
                      tryAgain: true,
                    }));
                  }
            }
            disabled={!scanSettings.error}
            variant="secondary"
          >
            {scanSettings.successful ? 'Cancel' : 'Scan Again'}
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};
